/**
 * ConfigureImplementationModal.jsx
 *
 * Modal for configuring an AI agent to implement a requirement.
 * Matches the "Configure Implementation" modal from the DLC screenshots:
 * - Software Item (SWI) dropdown
 * - AI Agent dropdown
 * - Optional design.md textarea
 * - Readiness checklist
 */

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { getAvailableAgents } from '../../services/dlcService';
import './ConfigureImplementationModal.css';

// Readiness check helper
function computeReadiness(swiId, agentId, hasDesignMd) {
  return [
    { key: 'swi',    label: 'Link SRS to a Software Item (SWI)', done: !!swiId },
    { key: 'agent',  label: 'Select AI Agent for implementation', done: !!agentId },
    { key: 'design', label: 'design.md present in SWI repository', done: hasDesignMd, warning: !hasDesignMd },
    { key: 'detail', label: 'Detailed design document (optional)', done: false, optional: true },
  ];
}

export default function ConfigureImplementationModal({ requirement, projectContext, onSave, onClose }) {
  const [agents, setAgents] = useState([]);
  const [selectedAgentId, setSelectedAgentId] = useState(requirement?.agentConfig?.agentId || '');
  const [swiId, setSwiId] = useState(requirement?.swiId || '');
  const [swiName, setSwiName] = useState(requirement?.agentConfig?.swiName || '');
  const [designMd, setDesignMd] = useState(requirement?.agentConfig?.designMd || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getAvailableAgents().then(setAgents).catch(() => setAgents([]));
  }, []);

  const selectedAgent = agents.find(a => a.id === selectedAgentId);
  const hasDesignMd = designMd.trim().length > 0;
  const checks = computeReadiness(swiId, selectedAgentId, hasDesignMd);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        swiId,
        agentConfig: {
          agentId: selectedAgentId,
          agentName: selectedAgent?.name || selectedAgentId,
          swiId,
          swiName,
          designMd,
        },
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="dlc-modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="dlc-modal configure-modal">
        {/* Header */}
        <div className="dlc-modal-header">
          <div className="dlc-modal-icon configure-icon">⚙</div>
          <div className="dlc-modal-title-group">
            <div className="dlc-modal-title">Configure Implementation</div>
            {requirement && (
              <div className="dlc-modal-subtitle">
                {requirement.id} — {requirement.title}
              </div>
            )}
          </div>
          <button className="dlc-modal-close" onClick={onClose} type="button">×</button>
        </div>

        {/* Readiness checklist */}
        <div className="dlc-readiness">
          <div className="dlc-readiness-title">Implementation Readiness</div>
          {checks.map(c => (
            <div key={c.key} className={`dlc-readiness-item ${c.done ? 'done' : c.warning ? 'warning' : c.optional ? 'optional' : 'pending'}`}>
              <span className="dlc-readiness-icon">
                {c.done ? '✓' : c.warning ? '⚠' : '○'}
              </span>
              <span>{c.label}</span>
            </div>
          ))}
        </div>

        {/* Form */}
        <div className="dlc-form">
          {/* Software Item */}
          <label className="dlc-label">
            Software Item (SWI)
            <input
              className="dlc-input"
              placeholder="e.g. SWI-REPORT-001"
              type="text"
              value={swiId}
              onChange={e => setSwiId(e.target.value)}
            />
          </label>
          <label className="dlc-label">
            SWI Name (optional)
            <input
              className="dlc-input"
              placeholder="e.g. Report Generation Engine"
              type="text"
              value={swiName}
              onChange={e => setSwiName(e.target.value)}
            />
          </label>

          {/* AI Agent */}
          <label className="dlc-label">
            AI Agent
            <select
              className="dlc-select"
              value={selectedAgentId}
              onChange={e => setSelectedAgentId(e.target.value)}
            >
              <option value="">Select an agent...</option>
              {agents.map(a => (
                <option key={a.id} value={a.id}>
                  {a.name}{a.model ? ` (${a.model})` : ''}
                </option>
              ))}
            </select>
          </label>

          {/* Design document */}
          <label className="dlc-label">
            Detailed Design (optional)
            <textarea
              className="dlc-textarea"
              placeholder={'# Detailed Design\n\n## Overview\nDescribe the specific implementation approach...\n\n## Data Flow\n1. Input processing\n2. Business logic\n3. Output formatting'}
              rows={6}
              value={designMd}
              onChange={e => setDesignMd(e.target.value)}
            />
          </label>
        </div>

        {/* Footer */}
        <div className="dlc-modal-footer">
          <button className="dlc-btn dlc-btn-secondary" onClick={onClose} type="button">
            Cancel
          </button>
          <button
            className="dlc-btn dlc-btn-primary"
            disabled={saving || !selectedAgentId}
            onClick={handleSave}
            type="button"
          >
            {saving ? 'Saving…' : 'Save Configuration'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

ConfigureImplementationModal.propTypes = {
  requirement: PropTypes.object,
  projectContext: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};
